/**
 * Web Lab preview harness.
 *
 * Builds the document that goes into the Web Lab's <iframe srcDoc>: the student's
 * own HTML, wrapped by an injected prelude and runner that shim localStorage, catch
 * runtime errors, and run the lesson's DOM assertions back to the parent.
 *
 * ── Why the sandbox shapes all of this ────────────────────────────────────────
 * The preview runs with sandbox="allow-scripts" and deliberately WITHOUT
 * allow-same-origin. Granting both together effectively disables the sandbox: the
 * frame becomes same-origin with the app and could read the parent's localStorage,
 * which is where Firebase keeps auth tokens. Student- and AI-authored code runs in
 * here, so it must not have that reach.
 *
 * Two consequences fall out of that choice:
 *
 *   1. The frame's origin is "null", so the parent cannot authenticate messages by
 *      event.origin. It matches on event.source plus the per-run nonce below.
 *
 *   2. An origin-null document THROWS on any localStorage access
 *      (SecurityError: localStorage is not available for opaque origins). Lessons
 *      about saving data would be impossible, so the prelude installs a
 *      localStorage shim that proxies to the parent over postMessage. Students
 *      write ordinary setItem/getItem, the parent persists the values, and the data
 *      genuinely survives a reload — with the sandbox still closed.
 *
 * ── Why this is injected in two pieces ────────────────────────────────────────
 * The PRELUDE must run before ANY student script: a lesson whose very first line
 * is `localStorage.getItem(...)` would otherwise throw before the shim exists, and
 * an error listener installed at the end of the body never sees an error thrown
 * during parsing. The RUNNER must run last, once the student's markup and scripts
 * have been parsed. Injecting a single block at the end of <body> fails both cases
 * — that was a real bug caught by running this against jsdom.
 */

/**
 * Assertion vocabulary understood by the runner. Deliberately a small, structured
 * set rather than free-form JS: a generated string of JavaScript is unreliable and
 * unreviewable, whereas these are easy for the lesson generator to emit correctly
 * and easy for a human to read.
 *
 *   { description, selector, exists: true|false }
 *   { description, selector, textContains: "..." }
 *   { description, selector, countAtLeast: 3 }
 *   { description, selector, cssProperty: "background-color", isNot: "rgba(0, 0, 0, 0)" }
 *   { description, selector, cssProperty: "color", equals: "rgb(255, 0, 0)" }
 *   { description, click: "#addBtn", then: { selector: ".item", countAtLeast: 1 } }
 *   { description, type: { selector: "#name", value: "Rosa" }, click: "#save",
 *     then: { selector: ".item", textContains: "Rosa" } }
 *   { description, storageKey: "misDatos", contains: "Rosa" }
 */

// Runs FIRST, before any student markup or script.
const PRELUDE_SOURCE = `
(function () {
  var NONCE = "__NONCE__";
  var STORE = __STORE__;

  function post(msg) {
    try {
      msg.nonce = NONCE;
      parent.postMessage(msg, "*");
    } catch (e) { /* nothing we can do from in here */ }
  }
  window.__weblabPost = post;

  // ---- localStorage shim (see the file header for why this is necessary) ----
  // Writes are mirrored to the parent, which owns persistence. Reads are served
  // from the snapshot the parent handed us at load, so they stay synchronous —
  // the real localStorage API is synchronous and student code depends on that.
  try {
    var mem = STORE || {};
    var shim = {
      getItem: function (k) {
        return Object.prototype.hasOwnProperty.call(mem, String(k)) ? mem[String(k)] : null;
      },
      setItem: function (k, v) {
        mem[String(k)] = String(v);
        post({ type: "weblab:storage", op: "set", key: String(k), value: String(v) });
      },
      removeItem: function (k) {
        delete mem[String(k)];
        post({ type: "weblab:storage", op: "remove", key: String(k) });
      },
      clear: function () {
        mem = {};
        post({ type: "weblab:storage", op: "clear" });
      },
      key: function (i) {
        var keys = Object.keys(mem);
        return keys[i] != null ? keys[i] : null;
      },
    };
    Object.defineProperty(shim, "length", { get: function () { return Object.keys(mem).length; } });
    // Defines an OWN property that shadows the Window.prototype accessor. It never
    // invokes the real getter, so it works even where that getter would throw.
    Object.defineProperty(window, "localStorage", { value: shim, configurable: true });
  } catch (e) {
    // If the browser won't let us redefine it, lessons still run — only the
    // saving lessons are affected, and they surface it as a failed assertion.
  }

  // ---- Surface runtime errors instead of failing silently ----
  window.addEventListener("error", function (e) {
    post({ type: "weblab:error", message: String((e && e.message) || e) });
  });
  window.addEventListener("unhandledrejection", function (e) {
    post({ type: "weblab:error", message: String((e && e.reason) || e) });
  });
})();
`;

// Runs LAST, after the student's markup and scripts have been parsed.
const RUNNER_SOURCE = `
(function () {
  var NONCE = "__NONCE__";
  var TESTS = __TESTS__;

  var post = window.__weblabPost || function (msg) {
    try { msg.nonce = NONCE; parent.postMessage(msg, "*"); } catch (e) {}
  };

  function check(spec) {
    // A "then" clause reuses the same matcher shape, so both share this function.

    // Storage matcher — the only way to verify a "saving data" lesson. Whether
    // something was persisted is invisible in the DOM, so a lesson whose whole
    // point is localStorage cannot be graded by looking at the page.
    if (spec.storageKey) {
      var stored = window.localStorage.getItem(spec.storageKey);
      if (stored == null) return false;
      if (typeof spec.contains === "string") {
        return String(stored).toLowerCase().indexOf(spec.contains.toLowerCase()) !== -1;
      }
      return String(stored).length > 0;
    }

    if (spec.exists === false) {
      return document.querySelector(spec.selector) === null;
    }

    var nodes = spec.selector ? document.querySelectorAll(spec.selector) : [];

    if (typeof spec.countAtLeast === "number") {
      return nodes.length >= spec.countAtLeast;
    }

    var el = nodes[0];
    if (!el) return false;

    if (typeof spec.textContains === "string") {
      // Match against the whole matched set, not just the first node — "a row
      // containing Rosa" shouldn't depend on which row happens to be first.
      var needle = spec.textContains.toLowerCase();
      for (var i = 0; i < nodes.length; i++) {
        if ((nodes[i].textContent || "").toLowerCase().indexOf(needle) !== -1) return true;
      }
      return false;
    }

    if (spec.cssProperty) {
      var actual = window.getComputedStyle(el).getPropertyValue(spec.cssProperty).trim();
      if (typeof spec.isNot === "string") return actual !== spec.isNot;
      if (typeof spec.equals === "string") return actual === spec.equals;
      return actual.length > 0;
    }

    // Bare { selector } or { selector, exists: true }
    return true;
  }

  function runOne(test) {
    try {
      if (test.type && test.type.selector) {
        var input = document.querySelector(test.type.selector);
        if (!input) return false;
        input.value = test.type.value == null ? "" : String(test.type.value);
        input.dispatchEvent(new Event("input", { bubbles: true }));
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }

      if (test.click) {
        var target = document.querySelector(test.click);
        if (!target) return false;
        target.click();
      }

      return check(test.then ? test.then : test);
    } catch (e) {
      return false;
    }
  }

  function runAll() {
    var results = [];
    for (var i = 0; i < TESTS.length; i++) {
      results.push({
        description: TESTS[i].description || ("Check " + (i + 1)),
        passed: !!runOne(TESTS[i]),
      });
    }
    post({ type: "weblab:results", results: results });
  }

  // Give the student's own scripts a turn to run and paint before asserting.
  if (document.readyState === "complete" || document.readyState === "interactive") {
    setTimeout(runAll, 60);
  } else {
    window.addEventListener("DOMContentLoaded", function () { setTimeout(runAll, 60); });
  }
})();
`;

// Replacer FUNCTIONS, never plain strings: `String.replace` treats `$&`, `` $` ``,
// `$'` and `$$` as special in a string replacement, and test descriptions and saved
// values are free text that can easily contain them. Getting this wrong corrupts
// the injected script rather than just the visible text.
const fill = (source, values) =>
    Object.keys(values).reduce(
        (acc, token) => acc.replace(token, () => values[token]),
        source
    );

/**
 * Compose the srcDoc for a preview run.
 *
 * @param {string} html     The student's document.
 * @param {Array}  domTests Assertions to run (may be empty — then it's preview-only).
 * @param {string} nonce    Per-run token; the parent ignores messages without it.
 * @param {object} store    Snapshot of this lesson's persisted localStorage values.
 * @returns {string}
 */
export const buildWebLabSrcDoc = (html, domTests, nonce, store) => {
    const prelude = `<script>${fill(PRELUDE_SOURCE, {
        __NONCE__: String(nonce),
        __STORE__: JSON.stringify(store || {}),
    })}</script>`;

    const runner = `<script>${fill(RUNNER_SOURCE, {
        __NONCE__: String(nonce),
        __TESTS__: JSON.stringify(Array.isArray(domTests) ? domTests : []),
    })}</script>`;

    let doc = typeof html === "string" ? html : "";

    // Prelude as early as the document allows — after <head>, else after the <body>
    // opening tag, else at the very top (a bare fragment, which is what a beginner's
    // first attempt usually looks like). Never before <html>, which would be invalid.
    if (/<head[^>]*>/i.test(doc)) {
        doc = doc.replace(/<head[^>]*>/i, (m) => `${m}${prelude}`);
    } else if (/<body[^>]*>/i.test(doc)) {
        doc = doc.replace(/<body[^>]*>/i, (m) => `${m}${prelude}`);
    } else {
        doc = prelude + doc;
    }

    // Runner last, so the student's markup and scripts are already parsed.
    if (/<\/body\s*>/i.test(doc)) {
        return doc.replace(/<\/body\s*>/i, () => `${runner}</body>`);
    }
    return doc + runner;
};

export default buildWebLabSrcDoc;
